import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useFormSkin } from "./FormSkinContext";
import { useRequiredIndicator } from "./RequiredIndicatorContext";

/**
 * Label and required-asterisk classes per form skin (see FormSkinContext).
 * RadioGroup and StateLicensesEditor use them for their `<legend>`.
 */
export const LABEL_SKIN = {
  styleguide: {
    label: "text-slate-teal block text-[13px] font-bold uppercase leading-[1.5] tracking-[0.05em]",
    asterisk: "text-danger ml-0.5",
  },
  enroll: {
    label: "text-midnight block text-[13px] font-semibold leading-[1.5]",
    asterisk: "text-primary-text ml-0.5",
  },
} as const;

interface FieldLabelProps {
  htmlFor: string;
  children: ReactNode;
  /** Render an asterisk after the label text, independent of the native `required` attribute. */
  required?: boolean | undefined;
  /** Helper text rendered below the label, above the input. */
  hint?: string | undefined;
  /** Optional content rendered to the right of the label text (e.g. an InfoTooltip). */
  addon?: ReactNode | undefined;
  /**
   * When true, render `addon` right after the label text instead of at the far
   * end of the row. The default keeps the `justify-between` layout that callers
   * such as the partner NPI field rely on.
   */
  addonInline?: boolean | undefined;
  /**
   * Visually hide the label row (`sr-only`); screen readers still get the
   * label. A visible hint still renders below. For a control whose question
   * is a visible heading right above it, where a second label reads as a
   * duplicate.
   */
  visuallyHidden?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Label used by Input, Select and Textarea.
 *
 * It is exported so callers can label a group of inputs (e.g. an address
 * sub-form) without an `htmlFor` target: pass an empty string, and use a
 * surrounding `<fieldset>`/`<legend>` when the group needs stronger semantics.
 */
export function FieldLabel({
  htmlFor,
  children,
  required,
  hint,
  addon,
  addonInline,
  visuallyHidden,
  className,
}: FieldLabelProps): ReactNode {
  // Opt-in "(required)" wording (see RequiredIndicatorContext). `null` (the
  // default, and every non-opted-in SPA) keeps the asterisk-only rendering.
  const requiredWord = useRequiredIndicator();
  const skin = LABEL_SKIN[useFormSkin()];
  return (
    <div className={cn(!(visuallyHidden && !hint) && "mb-1.5", className)}>
      <div
        className={cn(
          "flex items-center",
          addonInline ? "gap-1.5" : "justify-between gap-2",
          visuallyHidden && "sr-only",
        )}
      >
        <label htmlFor={htmlFor} className={skin.label}>
          {children}
          {required && (
            <span aria-hidden="true" className={skin.asterisk}>
              *
            </span>
          )}
          {required && requiredWord && (
            <span className="text-slate-teal ml-1 font-normal">({requiredWord})</span>
          )}
        </label>
        {addon}
      </div>
      {hint && <p className="text-slate-teal mt-0.5 text-[13px] leading-[1.5]">{hint}</p>}
    </div>
  );
}
